import { useMemo, useState } from "react";

// Mirrors the server's `Subject.order(:kind, :name)` so newly-added subjects
// land in the same spot they would after a page reload.
const KIND_ORDER = { basic_science: 0, clinical_science: 1 };

function sortSubjects(subjects) {
  return [...subjects].sort((a, b) => {
    const byKind = (KIND_ORDER[a.kind] ?? 99) - (KIND_ORDER[b.kind] ?? 99);
    if (byKind !== 0) return byKind;
    return a.name.localeCompare(b.name);
  });
}

// Replaces the "Covered subjects" checkbox grid on the admin course-module form.
// Renders checkboxes that submit with the surrounding Rails form (same
// `field_name`), plus an inline control that POSTs a new subject to the admin
// subjects endpoint and adds it to the grid, pre-checked.
export default function CourseModuleSubjects({
  subjects = [],
  selected_ids = [],
  field_name,
  create_endpoint,
  csrf_token,
}) {
  const [list, setList] = useState(subjects);
  const [selected, setSelected] = useState(() => new Set(selected_ids));
  const [name, setName] = useState("");
  const [kind, setKind] = useState("basic_science");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const sorted = useMemo(() => sortSubjects(list), [list]);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function addSubject() {
    const trimmed = name.trim();
    if (!trimmed || saving) return;

    setSaving(true);
    setError(null);
    try {
      const res = await fetch(create_endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf_token,
          Accept: "application/json",
        },
        body: JSON.stringify({ subject: { name: trimmed, kind } }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Could not add subject.");
        return;
      }
      setList((prev) =>
        prev.some((s) => s.id === data.id) ? prev : [...prev, data]
      );
      setSelected((prev) => new Set(prev).add(data.id));
      setName("");
    } catch {
      setError("Network error — please try again.");
    } finally {
      setSaving(false);
    }
  }

  function onKeyDown(e) {
    // Enter in the name field adds the subject without submitting the module form.
    if (e.key === "Enter") {
      e.preventDefault();
      addSubject();
    }
  }

  return (
    <div data-testid="course-module-subjects">
      {/* Mirrors Rails collection_check_boxes: a single blank entry so an empty
          selection still submits an array and clears the association. */}
      <input type="hidden" name={field_name} value="" />

      <div
        className="admin-checkbox-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: "8px",
        }}
      >
        {sorted.map((s) => (
          <label key={s.id} className="admin-checkbox">
            <input
              type="checkbox"
              name={field_name}
              value={s.id}
              checked={selected.has(s.id)}
              onChange={() => toggle(s.id)}
            />{" "}
            {s.name}
          </label>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          marginTop: "12px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          className="admin-input"
          style={{ flex: "1 1 200px", minWidth: "180px" }}
          placeholder="Add a subject not listed above"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label="New subject name"
        />
        <select
          className="admin-input"
          style={{ flex: "0 0 auto", width: "auto" }}
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          aria-label="New subject classification"
        >
          <option value="basic_science">Basic science</option>
          <option value="clinical_science">Clinical science</option>
        </select>
        <button
          type="button"
          className="admin-btn --accent --sm"
          onClick={addSubject}
          disabled={saving || !name.trim()}
        >
          {saving ? "Adding…" : "Add"}
        </button>
      </div>

      {error && (
        <div
          role="alert"
          style={{ color: "#c0392b", fontSize: "13px", marginTop: "6px" }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
